import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDebounceFn } from '@vueuse/core'
import { useSearchStore } from '../stores/searchStore'
import { useAdBlockDetector } from './useAdBlockDetector'

export function useNavBarSearch() {
  const router = useRouter()
  const route = useRoute()
  const { t } = useI18n()
  const searchStore = useSearchStore()
  const { isAdBlockerDetected } = useAdBlockDetector()

  const searchQuery = ref('')
  const searchScope = ref('artists')
  const advancedParams = ref({ artist: '', title: '' })
  const showAdBlockModal = ref(false)
  const mobileMenuOpen = ref(false)
  const searchInputRef = ref(null)

  const searchScopeOptions = computed(() => [
    { value: 'artists', label: t('nav.artists') },
    { value: 'albums', label: t('nav.albums') },
    { value: 'concerts', label: t('nav.concerts') },
    { value: 'festivals', label: t('nav.festivals') },
    { value: 'album_advanced', label: t('nav.advanced') }
  ])

  const getPlaceholder = computed(() => {
    switch (searchScope.value) {
      case 'artists': return t('search.placeholder_artists')
      case 'albums': return t('search.placeholder_albums')
      case 'concerts': return t('search.placeholder_concerts')
      case 'festivals': return t('search.placeholder_festivals')
      default: return t('search.placeholder_default')
    }
  })

  const loginRedirect = computed(() => {
    const currentPath = route.fullPath
    return currentPath !== '/' ? { path: '/login', query: { redirect: currentPath } } : '/login'
  })

  const debouncedSearch = useDebounceFn(() => {
    handleSearch()
  }, 500)

  function handleSearch() {
    if (isAdBlockerDetected.value) {
      showAdBlockModal.value = true
      return
    }

    const query = {}

    if (searchScope.value !== 'album_advanced') {
      if (!searchQuery.value.trim()) return
      query.q = searchQuery.value
      if (searchScope.value !== 'all') {
        query.type = searchScope.value
      }
    } else {
      if (!advancedParams.value.artist && !advancedParams.value.title) return
      if (advancedParams.value.artist) query.artist = advancedParams.value.artist
      if (advancedParams.value.title) query.title = advancedParams.value.title
      query.type = 'album_advanced'
    }

    router.push({ name: 'search', query })
    if (!mobileMenuOpen.value) return
  }

  function handleManualSearch(event, closeMobileMenu, closeMobileSearch) {
    handleSearch()
    if (closeMobileMenu) closeMobileMenu()
    if (closeMobileSearch) closeMobileSearch()
  }

  function handleLogoClick(event, closeMobileMenu) {
    searchQuery.value = ''
    advancedParams.value = { artist: '', title: '' }
    searchScope.value = 'artists'
    searchStore.clearResults()
    if (closeMobileMenu) closeMobileMenu()
  }

  function clearSearch() {
    searchQuery.value = ''
    searchInputRef.value?.focus()
  }

  function closeAdBlockModal(clearAdBlockDetection) {
    showAdBlockModal.value = false
    if (clearAdBlockDetection) clearAdBlockDetection()
  }

  return {
    searchQuery,
    searchScope,
    advancedParams,
    showAdBlockModal,
    mobileMenuOpen,
    searchInputRef,
    searchScopeOptions,
    getPlaceholder,
    loginRedirect,
    debouncedSearch,
    handleSearch,
    handleManualSearch,
    handleLogoClick,
    clearSearch,
    closeAdBlockModal
  }
}
